import { GoogleTagManager } from '@next/third-parties/google';
import { AppProps } from 'next/app';
import { useEffect } from 'react';

import { useRouter } from '@boss/hooks';

import { useEventTracker } from '../../hooks/use-event-tracker/use-event-tracker';

type Props = {
  children: React.ReactNode;
  pageProps: AppProps['pageProps'];
  gtmId: string;
};

const getLabel = (element: HTMLElement) => {
  return (
    element.innerText ||
    element.id ||
    (element instanceof HTMLAnchorElement ? element.href : null) ||
    (element.getAttribute('data-icon') ? `link icon: ${element.getAttribute('data-icon')}` : '')
  );
};

const getTrackInfo = (element: HTMLElement) => {
  const trackInfo = element.getAttributeNames().reduce((acc, attr) => {
    if (attr.startsWith('data-track-')) {
      const key = attr.replace('data-track-', '');

      acc[key] = element.getAttribute(attr) ?? '';
    }
    return acc;
  }, {} as Record<string, string>);

  return {
    event: trackInfo.customevent,
    data: Object.keys(trackInfo).reduce((acc, key) => {
      if (key !== 'customevent') {
        acc[key] = trackInfo[key];
      }
      return acc;
    }, {} as Record<string, string>),
  };
};

const GoogleTagManagerProvider = ({ children, pageProps, gtmId }: Props) => {
  const { trackPageView, trackLinkClick, trackCustomEvent } = useEventTracker();
  const router = useRouter();

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Type assertion to ensure target is an HTMLElement
      const target = event.target as HTMLElement;

      // Find the nearest button or link element, starting from the clicked element
      const clickableElement = target.closest('button, a, [role="link"]') as HTMLElement;

      if (clickableElement) {
        const isButton = clickableElement.tagName === 'BUTTON';
        const label =
          getLabel(clickableElement) ||
          (target?.getAttribute('data-icon') ? `link icon: ${target?.getAttribute('data-icon')}` : '');
        const ariaLabel = target.getAttribute('aria-label');

        const trackInfo = getTrackInfo(clickableElement);

        if (trackInfo.event) {
          trackCustomEvent(trackInfo.event, {
            ...trackInfo.data,
          });
        }

        trackLinkClick({
          label,
          ariaLabel,
          type: isButton ? 'button' : 'link',
          pageInfo: {
            slug: router.asPath,
            pageTitle: pageProps.pageData?.pageTitle ?? router.asPath,
          },
        });
      }
    };

    document.addEventListener('click', handleClick);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('click', handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  useEffect(() => {
    trackPageView(pageProps.pageType);
  }, [pageProps, trackPageView]);

  return (
    <>
      {gtmId && <GoogleTagManager gtmId={gtmId} />}
      {children}
    </>
  );
};

export default GoogleTagManagerProvider;
